// App 版本下限（SPEC #data-496 identity.clients）：
//   硬下限 force_upgrade_below → 登入 / 註冊 / refresh 回 426 `UPGRADE_REQUIRED`（detail.force_upgrade_below）：
//     全域擋住、只能更新（refresh token 不作廢，更新後照常換）。
//   軟下限 min_supported_version → token 回應帶 `upgrade_recommended: true` + `min_supported_version`：
//     提示更新，每個本機日曆日最多一次、可略過（記在 localStorage）。
//
// 版本由 App 用 header `X-Kinio-App-Version` 報。**web 不帶這個 header**：plane Caddy 的 CORS 預檢只放行
// Authorization / Content-Type / X-Kinio-Client / Idempotency-Key / X-Share-Password，帶了會讓登入被瀏覽器擋掉；
// identity 沒帶 header 不擋也不建議（cmd/identity/upgrade.go）。web 一樣處理 426 / upgrade_recommended，
// 以便 identity 改用 client 判斷時不用再改。

import { KinioError } from './errors';

export interface UpgradeState {
  /** 426 UPGRADE_REQUIRED：擋住整個 App。 */
  required: boolean;
  /** detail.force_upgrade_below（顯示用；可能沒有）。 */
  forceBelow?: string;
  /** 軟下限：最近一次 token 回應的 min_supported_version（null = 不用提示）。 */
  recommended: string | null;
}

let state: UpgradeState = { required: false, recommended: null };
const listeners = new Set<(s: UpgradeState) => void>();

function emit(next: UpgradeState) {
  state = next;
  for (const fn of listeners) fn(state);
}

export const appUpgrade = {
  get(): UpgradeState {
    return state;
  },
  subscribe(fn: (s: UpgradeState) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  /** 426 → 擋住。 */
  require(forceBelow?: string): void {
    emit({ ...state, required: true, forceBelow });
  },
  /** token 回應 → 記下軟下限（沒有 upgrade_recommended = 清掉）。 */
  fromTokenResponse(data: { upgrade_recommended?: boolean; min_supported_version?: string } | null | undefined): void {
    const rec = data?.upgrade_recommended === true && typeof data.min_supported_version === 'string' && data.min_supported_version ? data.min_supported_version : null;
    if (rec !== state.recommended) emit({ ...state, recommended: rec });
  },
  /** 測試用。 */
  reset(): void {
    emit({ required: false, recommended: null });
  },
};

/** 錯誤是 426 UPGRADE_REQUIRED → 標成必須更新並回 true。 */
export function noteUpgradeRequired(e: unknown): boolean {
  if (!(e instanceof KinioError) || e.code !== 'UPGRADE_REQUIRED') return false;
  const fb = e.detail.force_upgrade_below;
  appUpgrade.require(typeof fb === 'string' && fb ? fb : undefined);
  return true;
}

export const UPGRADE_PROMPT_KEY = 'kinio.upgradePrompt.lastShownDay';

/** 本機日曆日 `YYYY-MM-DD`。 */
export function localDay(now: number = Date.now()): string {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 今天還沒提示過才提示（每天最多一次）。 */
export function shouldPromptUpgrade(lastShownDay: string | null, now: number = Date.now()): boolean {
  return lastShownDay !== localDay(now);
}

export function readUpgradePromptDay(): string | null {
  try {
    return globalThis.localStorage?.getItem(UPGRADE_PROMPT_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeUpgradePromptDay(now: number = Date.now()): void {
  try {
    globalThis.localStorage?.setItem(UPGRADE_PROMPT_KEY, localDay(now));
  } catch {
    /* 隱私模式：這次開著的頁面由元件自己記 */
  }
}
